/**
 * MER-477: connector-level health rollup, extracted from health.ts as a
 * plain .mjs module so the node:test suite can import it directly (the
 * fallbackWindow.mjs / tenantScope.mjs precedent). health.ts re-exports
 * everything here, so app imports are unchanged.
 *
 * The pipeline writes sync_log.entity_key in UPPERCASE (loader.py emits
 * e.g. SERVICE_EVENT) while the registry below uses lowercase keys, so
 * every lookup normalizes BOTH sides with toUpperCase(). Do not compare
 * raw entity keys anywhere in this file.
 */

/**
 * Static map from connector name to the canonical entities it
 * produces. Used to roll sync_log rows up into connector-level
 * status. Add a row here when a new connector lands.
 */
export const CONNECTOR_REGISTRY = [
  {
    name: "Navusoft",
    description: "Service events, invoices, AR aging, contracts",
    entityKeys: [
      "service_event",
      "invoice",
      "customer",
      "route",
      "ar_current",
      "ar_transactions",
      "site_contracts",
    ],
  },
  {
    name: "Samsara",
    description: "Vehicle safety + telematics",
    // MER-657: daily_miles is the second Samsara-fed entity; MER-669 adds
    // daily_engine (engine hours, idle hours, fuel), a separate Samsara call
    // with its own failure. The top-level `active` bit still ORs across these
    // keys, so a fresh `asset` sync alone reads "active"; MER-675 surfaces a
    // dead sub-feed through the per-entity `degraded` list instead.
    entityKeys: ["asset", "daily_miles", "daily_engine"],
  },
  {
    name: "Ramp",
    description: "Accounts payable transactions",
    entityKeys: ["ap_transactions"],
  },
  {
    name: "Pipeline (derived)",
    description: "KPI summary, route KPIs, financial health, flags",
    entityKeys: [
      "kpi_summary",
      "route_kpis",
      "financial_health",
      "top_ar_accounts",
      "contract_expirations",
      "insight",
      "benchmark",
      "flag",
      "audit_finding",
    ],
  },
];

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The state of one entity key inside the 7-day window:
 *   "ok"       a successful sync inside the window
 *   "failed"   its latest sync failed
 *   "stale"    its latest sync succeeded, but before the window
 *   "missing"  no sync_log row at all. Also what a STARVED feed looks like:
 *              SupabaseLoader.sync_entity early-returns on an empty frame
 *              without writing a row, so a dead feed is absent, not failed.
 */
function entityState(row, now) {
  if (!row?.lastSyncAt) return "missing";
  const ts = Date.parse(row.lastSyncAt);
  if (!Number.isFinite(ts)) return "missing";
  if (row.lastSyncStatus === "failed") return "failed";
  if (row.lastSyncStatus === "success" && now - ts < SEVEN_DAYS_MS) return "ok";
  return "stale";
}

/**
 * Roll entity health rows up into connector-level status. A connector
 * is "active" if any of its entityKeys has a successful sync within
 * the last 7 days. Last sync timestamp is the newest across the set.
 *
 * MER-675: `active` alone masked a dead sub-feed (a fresh `asset` sync kept
 * Samsara "active" while daily_miles was dead), because adding a key to an OR
 * can only ever add activity. The rollup bit is unchanged, so no connector's
 * top-level meaning moves; each connector now also carries `entities`, one
 * state per key, and `degraded`, the keys that are not "ok" while the
 * connector is active. A connector that is not active reports no degraded
 * keys: its own badge already says it is down or unconfigured.
 *
 * Entity keys are matched case-insensitively: the pipeline writes
 * UPPERCASE entity_keys to sync_log while CONNECTOR_REGISTRY uses
 * lowercase (MER-477).
 *
 * @param {Array<{ entityKey: string, lastSyncAt: string | null,
 *   lastSyncStatus: "success" | "failed" | null }>} entityHealth
 * @param {number} [now]   epoch ms for the 7-day activity window;
 *   injectable so tests are deterministic. Defaults to Date.now().
 */
export function deriveConnectorHealth(entityHealth, now = Date.now()) {
  const byKey = new Map(entityHealth.map((r) => [r.entityKey.toUpperCase(), r]));
  return CONNECTOR_REGISTRY.map((c) => {
    let lastSyncAt = null;
    let active = false;
    for (const key of c.entityKeys) {
      const row = byKey.get(key.toUpperCase());
      if (!row?.lastSyncAt) continue;
      const ts = Date.parse(row.lastSyncAt);
      if (!Number.isFinite(ts)) continue;
      if (!lastSyncAt || ts > Date.parse(lastSyncAt)) lastSyncAt = row.lastSyncAt;
      if (row.lastSyncStatus === "success" && now - ts < SEVEN_DAYS_MS) active = true;
    }
    const entities = c.entityKeys.map((key) => {
      const row = byKey.get(key.toUpperCase());
      return {
        key,
        state: entityState(row, now),
        lastSyncAt: row?.lastSyncAt ?? null,
      };
    });
    const degraded = active ? entities.filter((e) => e.state !== "ok") : [];
    return {
      name: c.name,
      description: c.description,
      entityKeys: c.entityKeys,
      lastSyncAt,
      active,
      entities,
      degraded,
    };
  });
}
